/**
 * LocationProvider
 *
 * Owns the visitor's consent state for browser geolocation and exposes
 * coords + source to the rest of the app. Two paths produce coords:
 *
 *   1. GPS — visitor grants `navigator.geolocation` permission and we
 *      take the lat/lon at face value.
 *   2. Zip fallback — after enough denials in a day, we ask for a US zip
 *      and the backend geocodes it once; we treat the resulting lat/lon
 *      as a permanent location source and stop reprompting.
 *
 * Daily reprompt budget
 *   The visitor sees at most `DAILY_REPROMPT_BUDGET` modal appearances
 *   per calendar day (1 initial + N reprompts = budget). The Nth (final)
 *   showing of the day swaps the "here's how to enable location" copy
 *   for the zip-fallback form so we still get a useful signal before
 *   going quiet for the day. The counter resets at the visitor's local
 *   midnight via a date-bucketed storage key.
 *
 * Reprompt trigger
 *   After a denial we wait for *either* `REPROMPT_ROUTE_THRESHOLD` route
 *   changes *or* `REPROMPT_MINUTES_THRESHOLD` minutes elapsed since the
 *   last modal — whichever happens first. Route changes are reported by
 *   the consumer (LocationGate in MainLayout) so the provider doesn't
 *   need a router dependency.
 *
 * Storage
 *   All state lives in a single localStorage key as a JSON blob so we
 *   can read it in one shot on mount and write it back atomically. We
 *   intentionally don't persist coords across sessions when the source
 *   is GPS — the OS handle on the permission is the source of truth, so
 *   on next load we either silently re-fetch or fall back to the
 *   denied/unknown path. Zip-source coords *do* persist (they don't
 *   require any browser permission).
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { lookupZip } from '@/utils/zipLookup';
import type {
    ConsentModalMode,
    LocationCoords,
    LocationSource,
    ZipFallbackDetails,
} from '@/types/location';

// ──────────────────────────────────────────────────────────────────────
// Tuneables. Module scope (not config) — these are behavioral defaults,
// not deployment knobs. If product wants to change them, change the
// constants in source.
// ──────────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'rews.location.consent.v1';
const DAILY_REPROMPT_BUDGET = 5;
const REPROMPT_ROUTE_THRESHOLD = 5;
const REPROMPT_MINUTES_THRESHOLD = 30;
const GEOLOCATION_TIMEOUT_MS = 8_000;
const GEOLOCATION_MAX_AGE_MS = 60_000;

// ──────────────────────────────────────────────────────────────────────
// Persisted state shape (single JSON blob in localStorage).
// ──────────────────────────────────────────────────────────────────────
type PermissionState = 'granted' | 'denied' | 'unknown';

interface PersistedConsent {
    permissionState: PermissionState;
    zipDetails: ZipFallbackDetails | null;
    lastRepromptAt: string | null;          // ISO timestamp
    routesSinceReprompt: number;
    repromptsToday: { date: string; count: number };  // YYYY-MM-DD bucket
}

const DEFAULT_PERSISTED: PersistedConsent = {
    permissionState: 'unknown',
    zipDetails: null,
    lastRepromptAt: null,
    routesSinceReprompt: 0,
    repromptsToday: { date: '', count: 0 },
};

// ──────────────────────────────────────────────────────────────────────
// Context surface — what consumers see.
// ──────────────────────────────────────────────────────────────────────
interface LocationContextValue {
    coords: LocationCoords | null;
    source: LocationSource;
    /** Drives the consent modal. `hidden` when nothing to show. */
    mode: ConsentModalMode;
    /** True once we've loaded localStorage + done the first-mount silent
     *  GPS check; child UI should avoid flashing the modal before this. */
    initialized: boolean;
    /** Trigger the native geolocation prompt. Records grant/deny. */
    requestGps: () => Promise<void>;
    /** Submit a zip code; backend geocodes and we persist as permanent. */
    submitZip: (zip: string) => Promise<void>;
    /** Visitor closed the modal without acting; tracks toward the daily
     *  budget so we don't immediately reopen on the next route change. */
    dismissModal: () => void;
    /** Called by route listener to advance the reprompt threshold. */
    notifyRouteChange: () => void;
    /** Error from the most recent action, surfaced for the modal UI. */
    lastError: string | null;
}

const LocationContext = createContext<LocationContextValue | undefined>(undefined);

// ──────────────────────────────────────────────────────────────────────
// Pure helpers
// ──────────────────────────────────────────────────────────────────────
const todayISO = (): string => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const minutesSince = (iso: string | null): number => {
    if (!iso) return Infinity;
    const ms = Date.now() - Date.parse(iso);
    return ms / 60_000;
};

const loadPersisted = (): PersistedConsent => {
    if (typeof localStorage === 'undefined') return { ...DEFAULT_PERSISTED };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULT_PERSISTED };
        const parsed = JSON.parse(raw) as PersistedConsent;
        // Normalize the daily bucket — if the persisted date isn't today,
        // it implicitly resets to zero for the new day.
        if (parsed.repromptsToday?.date !== todayISO()) {
            parsed.repromptsToday = { date: todayISO(), count: 0 };
        }
        return { ...DEFAULT_PERSISTED, ...parsed };
    } catch {
        return { ...DEFAULT_PERSISTED };
    }
};

const savePersisted = (state: PersistedConsent): void => {
    if (typeof localStorage === 'undefined') return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* private mode etc. — the state will be ephemeral, not fatal */
    }
};

/**
 * Pure derivation of the modal mode given the persisted state. Kept
 * pure so it's easy to unit-test the daily-budget + threshold logic
 * without dragging in the browser shell.
 */
function deriveMode(state: PersistedConsent): ConsentModalMode {
    if (state.zipDetails) return 'hidden';
    if (state.permissionState === 'granted') return 'hidden';
    if (state.permissionState === 'unknown') return 'first';

    // permissionState === 'denied' — daily budget gate.
    const countToday =
        state.repromptsToday.date === todayISO() ? state.repromptsToday.count : 0;
    if (countToday >= DAILY_REPROMPT_BUDGET) return 'hidden';

    const dueByRoutes = state.routesSinceReprompt >= REPROMPT_ROUTE_THRESHOLD;
    const dueByTime = minutesSince(state.lastRepromptAt) >= REPROMPT_MINUTES_THRESHOLD;
    if (!dueByRoutes && !dueByTime) return 'hidden';

    // The final showing of the day is the zip-fallback variant so we
    // still capture something useful before going quiet for the day.
    const willBeFinalShowing = countToday + 1 >= DAILY_REPROMPT_BUDGET;
    return willBeFinalShowing ? 'zip' : 'reprompt';
}

// ──────────────────────────────────────────────────────────────────────
// Browser geolocation wrapper. Promise-based so the provider can await.
// ──────────────────────────────────────────────────────────────────────
function getCurrentPositionPromise(): Promise<LocationCoords> {
    return new Promise((resolve, reject) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            reject(new Error('Geolocation is not available in this browser.'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            (err) => reject(new Error(err.message || 'Could not get your location.')),
            {
                enableHighAccuracy: false,
                timeout: GEOLOCATION_TIMEOUT_MS,
                maximumAge: GEOLOCATION_MAX_AGE_MS,
            },
        );
    });
}

// ──────────────────────────────────────────────────────────────────────
// Provider
// ──────────────────────────────────────────────────────────────────────
interface LocationProviderProps {
    children: ReactNode;
}

export function LocationProvider({ children }: LocationProviderProps) {
    const [persisted, setPersisted] = useState<PersistedConsent>(DEFAULT_PERSISTED);
    const [gpsCoords, setGpsCoords] = useState<LocationCoords | null>(null);
    const [initialized, setInitialized] = useState(false);
    const [lastError, setLastError] = useState<string | null>(null);

    // Latest persisted state without re-rendering on every read; useful
    // for the route-change notifier so it doesn't capture stale closures.
    const persistedRef = useRef(persisted);
    useEffect(() => {
        persistedRef.current = persisted;
    }, [persisted]);

    // First-mount: hydrate from storage, then probe `permissions.query`
    // and (if granted) silently refresh GPS coords. This is the "user
    // already approved on a previous visit" happy path.
    useEffect(() => {
        const loaded = loadPersisted();
        setPersisted(loaded);

        (async () => {
            if (loaded.zipDetails) {
                setInitialized(true);
                return;
            }
            if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
                try {
                    const result = await navigator.permissions.query({ name: 'geolocation' });
                    // TRUST THE BROWSER'S PERMISSION STATE as the source
                    // of truth. Update our persisted state up-front, BEFORE
                    // attempting a coords fetch. Previously we only flipped
                    // to 'granted' AFTER `getCurrentPositionPromise` came
                    // back — but that call can time out or hang even when
                    // permission is genuinely granted (cold GPS chip, VPN,
                    // etc.), which left our state stuck on 'denied' and
                    // kept the reprompt modal firing on every route
                    // change. Reflect the browser's truth immediately;
                    // the coords fetch is a best-effort follow-up.
                    const syncFromBrowser = (state: PermissionState) => {
                        const cur = persistedRef.current;
                        if (cur.permissionState === state) return;
                        const next: PersistedConsent = { ...cur, permissionState: state };
                        savePersisted(next);
                        setPersisted(next);
                    };

                    if (result.state === 'granted') {
                        syncFromBrowser('granted');
                        try {
                            const coords = await getCurrentPositionPromise();
                            setGpsCoords(coords);
                        } catch {
                            /* timeout / hang — permission is still granted;
                               coords will be re-attempted on the next
                               explicit requestGps() or by any consumer
                               that reads `coords`. Don't downgrade state. */
                        }
                    } else if (result.state === 'denied') {
                        syncFromBrowser('denied');
                    }

                    // Live-sync when the OS-level permission changes
                    // mid-session (visitor flips the toggle in browser
                    // settings). Without this, our persisted state gets
                    // stuck one step behind reality until the next
                    // full page reload.
                    if ('onchange' in result) {
                        result.onchange = () => {
                            if (result.state === 'granted') {
                                syncFromBrowser('granted');
                                getCurrentPositionPromise()
                                    .then(setGpsCoords)
                                    .catch(() => { /* non-fatal */ });
                            } else if (result.state === 'denied') {
                                syncFromBrowser('denied');
                                setGpsCoords(null);
                            }
                        };
                    }
                } catch {
                    /* Permissions API not available on older Safari — fine */
                }
            }
            setInitialized(true);
        })();
    }, []);

    // Persist on every state change so we never have to chase missed
    // saves in the action handlers below.
    useEffect(() => {
        if (!initialized) return;
        savePersisted(persisted);
    }, [persisted, initialized]);

    const requestGps = useCallback(async () => {
        setLastError(null);
        try {
            const coords = await getCurrentPositionPromise();
            setGpsCoords(coords);
            setPersisted((prev) => ({
                ...prev,
                permissionState: 'granted',
                // Reset reprompt accounting so a future revoke starts clean.
                routesSinceReprompt: 0,
                lastRepromptAt: null,
            }));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Could not get your location.';
            setLastError(message);
            // Ask the browser what the ACTUAL permission state is before
            // marking the visitor as denied. Timeouts / GPS-cold errors
            // don't mean permission was revoked — they mean "we couldn't
            // get a fix right now." Only flip to 'denied' when the
            // browser confirms it. Otherwise stay in whatever state we
            // were in so the reprompt modal doesn't fire on a transient
            // GPS failure.
            let browserState: PermissionState | null = null;
            try {
                if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
                    const result = await navigator.permissions.query({ name: 'geolocation' });
                    browserState = result.state as PermissionState;
                }
            } catch {
                /* Permissions API unavailable — fall through to legacy behavior */
            }

            if (browserState === 'denied') {
                setPersisted((prev) => recordReprompt({ ...prev, permissionState: 'denied' }));
            } else if (browserState === 'granted') {
                // Permission is granted but the coords fetch timed out.
                // Reflect that truth (in case our state was stale) and
                // DON'T record a reprompt slot for a transient failure.
                setPersisted((prev) => ({ ...prev, permissionState: 'granted' }));
            } else {
                // 'prompt' or unknown — legacy behavior: spend the slot.
                setPersisted((prev) => recordReprompt({ ...prev, permissionState: 'denied' }));
            }
        }
    }, []);

    const submitZip = useCallback(async (zip: string) => {
        setLastError(null);
        try {
            const details = await lookupZip(zip);
            if (!details) throw new Error('Could not look up that zip code.');
            setPersisted((prev) => ({
                ...prev,
                zipDetails: details,
                // Zip is treated as a permanent location source; we keep
                // permissionState as-is in case the user later grants GPS.
            }));
        } catch (err: unknown) {
            const detail =
                (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
                || (err instanceof Error ? err.message : 'Could not look up that zip code.');
            setLastError(detail);
            throw new Error(detail);
        }
    }, []);

    const dismissModal = useCallback(() => {
        // Treat any dismissal as a soft deny: if we were still at
        // 'unknown' (first-time prompt) this moves us into the reprompt
        // cycle, otherwise it just spends a daily slot.
        setPersisted((prev) =>
            recordReprompt({
                ...prev,
                permissionState: prev.permissionState === 'unknown' ? 'denied' : prev.permissionState,
            }),
        );
    }, []);

    const notifyRouteChange = useCallback(() => {
        // Skip increments when there's no point — the gate stays hidden
        // regardless once the visitor is in a settled state.
        const prev = persistedRef.current;
        if (prev.zipDetails || prev.permissionState === 'granted') return;
        setPersisted((p) => ({ ...p, routesSinceReprompt: p.routesSinceReprompt + 1 }));
    }, []);

    const mode = useMemo<ConsentModalMode>(() => {
        if (!initialized) return 'hidden';
        return deriveMode(persisted);
    }, [persisted, initialized]);

    const value = useMemo<LocationContextValue>(
        () => ({
            coords: gpsCoords || persisted.zipDetails || null,
            source: gpsCoords ? 'gps' : persisted.zipDetails ? 'zip' : null,
            mode,
            initialized,
            requestGps,
            submitZip,
            dismissModal,
            notifyRouteChange,
            lastError,
        }),
        [gpsCoords, persisted.zipDetails, mode, initialized, requestGps, submitZip, dismissModal, notifyRouteChange, lastError],
    );

    return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

/**
 * Bumps the daily counter, stamps the reprompt time, and clears the
 * route counter. Called whenever the modal is shown-and-dismissed or a
 * GPS request is rejected — both spend the same budget slot.
 */
function recordReprompt(prev: PersistedConsent): PersistedConsent {
    const today = todayISO();
    const sameDay = prev.repromptsToday.date === today;
    return {
        ...prev,
        lastRepromptAt: new Date().toISOString(),
        routesSinceReprompt: 0,
        repromptsToday: {
            date: today,
            count: (sameDay ? prev.repromptsToday.count : 0) + 1,
        },
    };
}

export function useLocation(): LocationContextValue {
    const ctx = useContext(LocationContext);
    if (!ctx) {
        throw new Error('useLocation must be used within a LocationProvider');
    }
    return ctx;
}
